import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { AdminStatsPayload, StatsRange } from "./apiTypes";

export type AdminNewDashProps = {
  /** Backoffice por magic link: stats vía `/api/backoffice/magic/...` sin cookie admin. */
  magicToken?: string;
};

type MagicStatsEnvelope = AdminStatsPayload & {
  survey: { id: string; slug: string; name: string };
  new_dash_meta?: {
    hypothesis: string;
    question_roles: {
      pain: string;
      urgency: string;
      workaround: string;
      wtp: string;
    };
  };
};

type VerdictState = "go" | "pivot" | "kill" | "collect";

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(100, Math.round(v)));
}

function verdictFromDimensions(
  responses: number,
  dims: { pain: number; urgency: number; workaround: number; wtp: number },
): VerdictState {
  if (responses < 30) return "collect";
  /** Cada dimensión “pasa” solo si está estrictamente por encima de 60 (no >=). */
  const passed = [dims.pain, dims.urgency, dims.workaround, dims.wtp].filter((x) => x > 60).length;
  if (passed === 4) return "go";
  if (passed >= 2) return "pivot";
  return "kill";
}

/** Preferir número del API incluso cuando es 0; no usar || (0 es válido). */
function finiteOr(zeroIfInvalid: () => number, v: number): number {
  return Number.isFinite(v) ? clamp01(v) : clamp01(zeroIfInvalid());
}

function verdictMeta(v: VerdictState): { emoji: string; title: string; cls: string } {
  if (v === "go") return { emoji: "🟢", title: "AVANZA", cls: "pp-verdict-go" };
  if (v === "pivot") return { emoji: "🟡", title: "PIVOTA", cls: "pp-verdict-pivot" };
  if (v === "kill") return { emoji: "🔴", title: "MATA", cls: "pp-verdict-kill" };
  return { emoji: "⚪", title: "SEGUÍ RECOLECTANDO", cls: "pp-verdict-collect" };
}

function levelText(score: number): string {
  if (score >= 75) return "Alta";
  if (score >= 60) return "Media";
  return "Baja";
}

export default function AdminNewDash(props?: AdminNewDashProps) {
  const { magicToken } = props ?? {};
  const [searchParams] = useSearchParams();
  const surveyId = searchParams.get("survey_id") ?? "";
  const [range, setRange] = useState<StatsRange>("1m");
  const [stats, setStats] = useState<AdminStatsPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [hypothesis, setHypothesis] = useState<string>("Sin hipótesis definida para esta encuesta.");
  const [coachMessages, setCoachMessages] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [coachInput, setCoachInput] = useState("");
  const [coachBusy, setCoachBusy] = useState(false);
  const [coachErr, setCoachErr] = useState<string | null>(null);

  const [questionRoles, setQuestionRoles] = useState<{
    pain: string;
    urgency: string;
    workaround: string;
    wtp: string;
  }>({
    pain: "q3_consequence",
    urgency: "q7_urgency",
    workaround: "q6_what_breaks",
    wtp: "q9_price_sense",
  });

  const load = useCallback(async () => {
    setErr(null);
    try {
      const q = new URLSearchParams({ range });
      let res: Response;
      if (magicToken) {
        res = await fetch(`/api/backoffice/magic/${encodeURIComponent(magicToken)}/stats?${q.toString()}`);
      } else {
        if (surveyId) q.set("survey_id", surveyId);
        res = await fetch(`/api/admin/stats?${q.toString()}`, { credentials: "include" });
      }
      if (!res.ok) {
        setErr(magicToken ? "Link inválido o expirado." : "No se pudieron cargar datos.");
        return;
      }
      const data = (await res.json()) as AdminStatsPayload | MagicStatsEnvelope;
      setStats(data);
      if (magicToken && "new_dash_meta" in data && data.new_dash_meta) {
        const m = data.new_dash_meta;
        setHypothesis(m.hypothesis);
        setQuestionRoles(m.question_roles);
      }
    } catch {
      setErr("Error de red.");
    }
  }, [range, surveyId, magicToken]);

  const loadHypothesis = useCallback(async () => {
    if (magicToken) {
      return;
    }
    if (!surveyId) {
      setHypothesis("Vista global sin una hipótesis única. Seleccioná una encuesta para comparar hipótesis vs evidencia.");
      return;
    }
    try {
      const res = await fetch("/api/admin/surveys", { credentials: "include" });
      if (!res.ok) return;
      const data = (await res.json()) as {
        items: { id: string; definition_json: string }[];
      };
      const selected = (data.items ?? []).find((s) => s.id === surveyId);
      if (!selected) return;
      const parsed = JSON.parse(selected.definition_json) as Record<string, unknown>;
      const ini =
        parsed.initiative && typeof parsed.initiative === "object"
          ? (parsed.initiative as Record<string, unknown>)
          : null;
      const summary = typeof ini?.summary === "string" ? ini.summary.trim() : "";
      const segment = typeof ini?.segment_hypothesis === "string" ? ini.segment_hypothesis.trim() : "";
      const roles =
        parsed.question_roles && typeof parsed.question_roles === "object"
          ? (parsed.question_roles as Record<string, unknown>)
          : {};
      const roleValue = (k: string, fallback: string) =>
        typeof roles[k] === "string" && (roles[k] as string).trim()
          ? (roles[k] as string).trim()
          : fallback;
      setQuestionRoles({
        pain: roleValue("pain", "q3_consequence"),
        urgency: roleValue("urgency", "q7_urgency"),
        workaround: roleValue("workaround", "q6_what_breaks"),
        wtp: roleValue("wtp", "q9_price_sense"),
      });
      if (summary || segment) {
        setHypothesis([summary, segment].filter(Boolean).join(" "));
      }
    } catch {
      // fallback text already loaded
    }
  }, [surveyId, magicToken]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadHypothesis();
  }, [loadHypothesis]);

  async function sendDashCoach() {
    if (magicToken) return;
    const trimmed = coachInput.trim();
    if (!trimmed) return;
    setCoachErr(null);
    setCoachBusy(true);
    const next = [...coachMessages, { role: "user" as const, content: trimmed }];
    setCoachMessages(next);
    setCoachInput("");
    try {
      const res = await fetch("/api/admin/dash-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          messages: next.map((m) => ({ role: m.role, content: m.content })),
          survey_id: surveyId || undefined,
          range,
        }),
      });
      const data = (await res.json()) as { reply?: string; error?: string };
      if (!res.ok || !data.reply) {
        setCoachErr(data.error ?? "Coach no disponible.");
        return;
      }
      setCoachMessages((prev) => [...prev, { role: "assistant", content: data.reply! }]);
    } catch {
      setCoachErr("Error de red.");
    } finally {
      setCoachBusy(false);
    }
  }

  const model = useMemo(() => {
    if (!stats) return null;
    if (stats.stats_template === "flexible_v1") {
      const validN = stats.p2_count;
      const fd = stats.flexible_dimensions ?? [];
      const pain = clamp01(fd[0]?.value ?? 0);
      const urgency = clamp01(fd[1]?.value ?? 0);
      const workaround = clamp01(fd[2]?.value ?? 0);
      const wtp = clamp01(fd[3]?.value ?? 0);
      const validationScore = clamp01((pain + urgency + workaround + wtp) / 4);
      const verdict = verdictFromDimensions(validN, { pain, urgency, workaround, wtp });
      const meta = verdictMeta(verdict);
      return {
        validN,
        pain,
        urgency,
        workaround,
        wtp,
        validationScore,
        verdict,
        meta,
        flexMode: true as const,
        flexDimensions: fd,
      };
    }
    const validN = stats.p2_count;
    const dist = stats.question_distributions ?? {};
    const roleShare = (id: string): number => {
      const rows = dist[id] ?? [];
      if (!rows.length || validN <= 0) return 0;
      const mentions = rows.reduce((acc, x) => acc + x.count, 0);
      return clamp01((mentions / validN) * 100);
    };
    const pain = finiteOr(() => roleShare(questionRoles.pain), stats.analysis.pain_p2.pct_alto);
    const urgency = finiteOr(
      () => roleShare(questionRoles.urgency),
      stats.kpis.pct_commitment_yes_maybe,
    );
    const rsBypass = roleShare(questionRoles.workaround);
    const hotPct =
      typeof stats.hot_pct_of_p2 === "number" && Number.isFinite(stats.hot_pct_of_p2)
        ? stats.hot_pct_of_p2
        : 0;
    const workaround = clamp01(rsBypass !== 0 ? rsBypass : hotPct);

    const spend = stats.analysis.payment.pct_spend_gt0;
    const q9gt20 = stats.analysis.payment.pct_q9_gt20;
    const payBlendAvg =
      typeof spend === "number" &&
      typeof q9gt20 === "number" &&
      Number.isFinite(spend) &&
      Number.isFinite(q9gt20)
        ? (spend + q9gt20) / 2
        : Number.NaN;
    const wtp = finiteOr(() => roleShare(questionRoles.wtp), payBlendAvg);
    const validationScore = clamp01((pain + urgency + workaround + wtp) / 4);
    const verdict = verdictFromDimensions(validN, { pain, urgency, workaround, wtp });
    const meta = verdictMeta(verdict);
    return { validN, pain, urgency, workaround, wtp, validationScore, verdict, meta };
  }, [stats, questionRoles]);

  if (!stats || !model) {
    return <p className="text-sm text-pp-muted">{err ?? "Cargando…"}</p>;
  }

  const summary = model.flexMode
    ? model.verdict === "go"
      ? "Las dimensiones declaradas en la encuesta superan el umbral en conjunto (N suficiente)."
      : model.verdict === "pivot"
        ? "Señal mixta en las dimensiones declaradas."
        : model.verdict === "kill"
          ? "Las dimensiones declaradas no superan los umbrales esperados."
          : "Seguí recolectando respuestas para esta encuesta flexible."
    : model.verdict === "go"
      ? "Hay señal consistente en dolor, urgencia, workaround y disposición de pago."
      : model.verdict === "pivot"
        ? "Hay evidencia parcial: existe dolor pero no cierra completo para esta propuesta."
        : model.verdict === "kill"
          ? "La evidencia actual no valida una oportunidad fuerte en esta hipótesis."
          : "La muestra todavía no alcanza para una decisión confiable.";

  const interpretation = `${stats.analysis.conclusion.slice(0, 2).join(" ")} ${stats.next_move}`.trim();
  const lastResponseText = stats.totals.total_responses > 0 ? "hay actividad reciente" : "sin actividad reciente";
  const dimsStrictlyPast60 =
    model.flexMode && model.flexDimensions.length > 0
      ? model.flexDimensions.filter((d) => d.value > 60).length
      : [model.pain, model.urgency, model.workaround, model.wtp].filter((d) => d > 60).length;
  const dimsTotal =
    model.flexMode && model.flexDimensions.length > 0 ? model.flexDimensions.length : 4;

  const responsesListPath = magicToken
    ? `/backoffice/m/${encodeURIComponent(magicToken)}/responses`
    : "/admin/responses";

  const responsesQuery = (extra: Record<string, string>) => {
    const q = new URLSearchParams(extra);
    if (!magicToken && surveyId) q.set("survey_id", surveyId);
    return q.toString();
  };

  const magicSurvey =
    magicToken && stats && "survey" in stats
      ? (stats as MagicStatsEnvelope).survey
      : undefined;

  return (
    <div className="space-y-6">
      {err && <p className="text-sm text-red-700">{err}</p>}

      {magicSurvey && (
        <section className="rounded-xl border border-pp-border bg-pp-card px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-pp-quiet">Encuesta (magic link)</p>
          <p className="text-lg font-semibold text-pp-main">{magicSurvey.name}</p>
          <p className="text-xs text-pp-muted font-mono mt-0.5">{magicSurvey.slug}</p>
        </section>
      )}

      {!magicToken && (
        <section className="rounded-xl border bg-pp-card p-4 space-y-3">
          <p className="text-sm font-medium text-pp-main">Coach Validation Score (Workers AI)</p>
          <p className="text-xs text-pp-muted">
            Preguntá en lenguaje natural; el backend inyecta stats del rango y encuesta actual.
          </p>
          {coachErr && <p className="text-xs text-red-700">{coachErr}</p>}
          <div className="max-h-48 overflow-auto space-y-2 text-sm">
            {coachMessages.map((m, i) => (
              <div
                key={`${m.role}-${i}`}
                className={`rounded-lg px-3 py-2 ${m.role === "user" ? "bg-pp-soft ml-6" : "bg-pp-soft/80 mr-6"}`}
              >
                <span className="text-[10px] uppercase text-pp-quiet font-mono">{m.role}</span>
                <p className="text-pp-main whitespace-pre-wrap">{m.content}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <textarea
              className="flex-1 border rounded px-3 py-2 text-sm min-h-[72px]"
              placeholder="Ej.: ¿Qué harías con 5 entrevistas más si el dolor sube pero WTP no?"
              value={coachInput}
              onChange={(e) => setCoachInput(e.target.value)}
            />
            <button
              type="button"
              disabled={coachBusy || !coachInput.trim()}
              onClick={() => void sendDashCoach()}
              className="self-stretch sm:self-auto px-4 py-2 rounded bg-pp-gradient text-white text-sm disabled:opacity-50"
            >
              {coachBusy ? "…" : "Enviar"}
            </button>
          </div>
        </section>
      )}

      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div
          className="inline-flex rounded-lg border border-pp-border p-0.5 bg-pp-soft"
          role="group"
          aria-label="Ventana de tiempo"
        >
          {(
            [
              ["1d", "1d"],
              ["1w", "1w"],
              ["1m", "1m"],
              ["all", "all"],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setRange(v)}
              className={`px-3 py-1.5 text-xs font-mono rounded-md transition-colors ${
                range === v ? "bg-pp-gradient text-white shadow-sm" : "text-pp-muted hover:bg-pp-card"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <section className={`rounded-2xl border border-pp-border p-5 ${model.meta.cls}`}>
        <p className="text-xs uppercase tracking-wider text-pp-muted">
          Vista 1 · Decisión
          {model.flexMode ? " · encuesta flexible" : ""}
        </p>
        <h2 className="mt-2 text-3xl font-semibold tracking-tight">
          {model.meta.emoji} {model.meta.title}
        </h2>
        <p className="mt-2 text-lg text-pp-main">
          Score de validación: <span className="font-semibold">{model.validationScore}/100</span>
        </p>
        <p className="mt-2 text-xs text-pp-muted leading-relaxed max-w-prose">
          Es el{" "}
          <span className="font-medium text-pp-main">promedio</span> de las cuatro barras de abajo. El veredicto no
          mira sólo ese número: cuenta cuántas dimensiones están <span className="font-medium text-pp-main">&gt; 60%</span>.
          Hoy pasan ese umbral{" "}
          <span className="font-medium tabular-nums">
            {dimsStrictlyPast60}/{dimsTotal}
          </span>
          . Con N ≥ 30 respuestas y cuatro dimensiones tipo Vínculos,{" "}
          <span className="font-medium">4/4 ⇒ AVANZA</span>, <span className="font-medium">2–3 ⇒ PIVOTA</span>,{" "}
          <span className="font-medium">0–1 ⇒ MATA</span>
          {model.flexMode ? " (aquí el umbral se aplica sobre las dimensiones declaradas)." : "."}
        </p>
        <p className="text-sm text-pp-muted">
          Basado en {model.validN}{" "}
          {model.flexMode ? "respuestas de esta encuesta." : "respuestas completas (P2 + P2n)."}
        </p>
        <p className="mt-3 text-sm text-pp-main">{summary}</p>
      </section>

      <section className="rounded-xl border bg-pp-card p-4">
        <p className="text-sm font-medium text-pp-main mb-3">
          {model.flexMode && model.flexDimensions.length > 0
            ? "Dimensiones declaradas (definición)"
            : "Las 4 dimensiones (por qué)"}
        </p>
        {model.flexMode && model.flexDimensions.length > 0
          ? model.flexDimensions.map((d) => {
              const v = Number(d.value);
              return (
                <div key={d.id} className="mb-3 last:mb-0">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="uppercase tracking-wide text-pp-muted">{d.label}</span>
                    <span className="font-mono text-pp-main">
                      {v}% · {levelText(v)}
                    </span>
                  </div>
                  <div className="h-2.5 rounded bg-pp-soft overflow-hidden">
                    <div className="h-full bg-pp-gradient rounded" style={{ width: `${v}%` }} />
                  </div>
                </div>
              );
            })
          : null}
        {!model.flexMode || model.flexDimensions.length === 0
          ? [
              ["Dolor", model.pain],
              ["Urgencia", model.urgency],
              ["Workarounds", model.workaround],
              ["WTP", model.wtp],
            ].map(([label, value]) => {
          const v = Number(value);
          return (
            <div key={label} className="mb-3 last:mb-0">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="uppercase tracking-wide text-pp-muted">{label}</span>
                <span className="font-mono text-pp-main">
                  {v}/100 · {levelText(v)}
                </span>
              </div>
              <div className="h-2.5 rounded bg-pp-soft overflow-hidden">
                <div className="h-full bg-pp-gradient rounded" style={{ width: `${v}%` }} />
              </div>
            </div>
          );
        })
          : null}
      </section>

      <section className="grid md:grid-cols-3 gap-4">
        <div className="rounded-xl border bg-pp-card p-4">
          <p className="text-xs uppercase tracking-wide text-pp-quiet mb-2">Hipótesis original</p>
          <p className="text-sm text-pp-main leading-relaxed">{hypothesis}</p>
        </div>
        <div className="rounded-xl border bg-pp-card p-4">
          <p className="text-xs uppercase tracking-wide text-pp-quiet mb-2">Evidencia recolectada</p>
          <ul className="text-sm text-pp-main space-y-1">
            <li>• {stats.analysis.pain_p2.pct_alto}% reporta dolor alto.</li>
            <li>• {stats.analysis.payment.pct_spend_gt0}% ya gasta para resolverlo.</li>
            <li>• {stats.kpis.pct_commitment_yes_maybe}% muestra compromiso yes/maybe.</li>
            <li>• {stats.analysis.payment.pct_q9_gt20}% tolera precio &gt; 20.</li>
          </ul>
        </div>
        <div className="rounded-xl border bg-pp-card p-4">
          <p className="text-xs uppercase tracking-wide text-pp-quiet mb-2">Interpretación</p>
          <p className="text-sm text-pp-main leading-relaxed">{interpretation || "Sin interpretación disponible."}</p>
        </div>
      </section>

      <section className="rounded-xl border bg-pp-card p-4">
        <p className="text-sm font-medium text-pp-main mb-2">Próximo experimento sugerido</p>
        <p className="text-sm text-pp-main">{stats.next_move}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link
            to={`${responsesListPath}?${responsesQuery({ signal: "hot" })}`}
            className="pp-btn-primary px-3 py-2 text-sm"
          >
            Ver leads calificados
          </Link>
          <Link
            to={`${responsesListPath}?${responsesQuery({ has_email: "1" })}`}
            className="pp-btn-secondary px-3 py-2 text-sm"
          >
            Exportables con email
          </Link>
        </div>
      </section>

      <section className="text-xs text-pp-muted rounded-xl border bg-pp-soft p-3">
        {model.flexMode ? (
          <>
            <span className="font-semibold text-pp-main">N = {model.validN}</span> respuestas completas en la ventana ·
            actividad: {lastResponseText}. Encuesta flexible: no hay P1/P2/P2n de Vínculos; cada respuesta es el
            cuestionario entero.
          </>
        ) : (
          <>
            {model.validN} respuestas completas · completion aprox{" "}
            {stats.totals.total_responses > 0 ? Math.round((model.validN / stats.totals.total_responses) * 100) : 0}% ·
            Última respuesta: {lastResponseText} · Distribución: P1 {stats.totals.total_p1} · P2 {stats.totals.total_p2}{" "}
            · P2n {stats.totals.total_p2n} · P3 {stats.totals.total_p3}
          </>
        )}
      </section>
    </div>
  );
}
